import {
  columnSizingFeature,
  columnVisibilityFeature,
  createPaginatedRowModel,
  createSortedRowModel,
  createTableHook,
  type RowData,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  type CellData,
  type TableFeatures,
} from "@tanstack/react-table-v9";
import { type ReactNode } from "react";

type RowAction<TData> = {
  label: string;
  icon?: ReactNode;
  onClick: (row: TData) => void;
  variant?: "default" | "destructive";
  disabled?: boolean | ((row: TData) => boolean);
  disabledTooltip?: React.ReactNode | ((row: TData) => React.ReactNode);
  hidden?: boolean | ((row: TData) => boolean);
};

declare module "@tanstack/react-table-v9" {
  interface ColumnMeta<
    TFeatures extends TableFeatures,
    TData extends RowData,
    TValue extends CellData = CellData,
  > {
    optional?: boolean;
    headerIcon?: ReactNode;
    width?: number | string;
    sticky?: boolean;
    align?: "left" | "right";
    cellOverlay?: string;
    verticalPadding?: string;
    horizontalPadding?: string;
  }

  interface TableMeta<TFeatures extends TableFeatures, TData extends RowData> {
    rowActions?: RowAction<TData>[];
    stickyOffsets?: Record<string, number>;
    lastStickyColumnId?: string;
  }
}

const features = tableFeatures({
  rowSelectionFeature,
  rowSortingFeature,
  rowPaginationFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
});

export const {
  useAppTable,
  createAppColumnHelper: createTableColumnHelper,
  useTableContext,
} = createTableHook({ features });

export type TableAppFeatures = typeof features;
export type { RowAction };
